import { useCallback, useEffect, useRef, useState } from "react";
import { type WebSocketMessage } from "../../providers/web_sockets_provider";
import { useConversationsAPI } from "./use_conversations_api";
import { ConversationsCreateData, useConversationsMessageHandler } from "./use_conversations_message_handler";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types

export type ResultsData = {
  data: ConversationsCreateData | undefined;
  error: Error | undefined;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Hook Logic

export function useConversationsSearch(
  rawQuery: string,
  debouncedQuery: string,
): {
  data: ConversationsCreateData | undefined;
  error: Error | undefined;
} {
  const { conversationsCreate, conversationsCancel } = useConversationsAPI();
  const activeQuery = useRef<string | undefined>(undefined);
  const { getResponse } = useConversationsMessageHandler();

  const [data, setData] = useState<ConversationsCreateData | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);

  const messageCallback = useCallback(
    (message: WebSocketMessage) => {
      const { query } = message;
      if (query !== activeQuery.current) {
        // Ignore messages not for our active query
        return;
      }

      // Process the message from the server
      const updatedState = getResponse(message);
      if (updatedState) {
        const { data: updatedData, error: updatedError } = updatedState;
        if (updatedData) {
          setData(updatedData);
          setError(undefined);
        } else if (updatedError) {
          setData((currentData) => {
            // Only set error if we don't have existing data
            if (!currentData) {
              setError(updatedError);
              return undefined;
            }
            return currentData;
          });
        }
      }
    },
    [getResponse],
  );

  const cancelRequests = useCallback(() => {
    if (activeQuery.current) {
      conversationsCancel(activeQuery.current);
      activeQuery.current = undefined;
    }
  }, [conversationsCancel]);

  // Trigger requests and cancellations when query changes
  useEffect(() => {
    setData(undefined);
    setError(undefined);

    if (debouncedQuery?.length && rawQuery === debouncedQuery) {
      // If there's a valid query, and it's been debounced, send a create request
      activeQuery.current = debouncedQuery;
      conversationsCreate(debouncedQuery, messageCallback);
    } else {
      cancelRequests();
    }

    return () => {
      cancelRequests();
    };
  }, [cancelRequests, conversationsCreate, debouncedQuery, messageCallback, rawQuery]);

  return { data, error };
}
